// Talking Family Tree — response parsing + grouping helpers.
//
// The exact JSON:API attribute names returned by GET /family_tree are not in the
// backend docs, so members are parsed defensively across the most likely keys
// (kebab-case is the convention elsewhere in this app). See TALKING_FAMILY_TREE_PLAN.md.

import { GROUPS, groupForRelationship } from './constants';

const pick = (attrs, keys, fallback = undefined) => {
  for (const key of keys) {
    if (attrs && attrs[key] !== undefined && attrs[key] !== null && attrs[key] !== '') {
      return attrs[key];
    }
  }
  return fallback;
};

// Build a quick {type -> {id -> resource}} index from JSON:API `included`.
const buildIncludedIndex = (included = []) => {
  const index = {};
  included.forEach((res) => {
    if (!res || !res.type) return;
    index[res.type] = index[res.type] || {};
    index[res.type][res.id] = res;
  });
  return index;
};

const resolveImage = (resource, includedIndex) => {
  // The avatar may come inline as an attribute or as a relationship to an image.
  const rel = resource?.relationships?.image?.data || resource?.relationships?.avatar?.data;
  if (rel && includedIndex?.[rel.type]?.[rel.id]) {
    return includedIndex[rel.type][rel.id].attributes || {};
  }
  return undefined;
};

const resolveVideo = (resource, includedIndex) => {
  // The video may come inline as an attribute or as a relationship to a video.
  const rel = resource?.relationships?.video?.data;
  if (rel && includedIndex?.[rel.type]?.[rel.id]) {
    return includedIndex[rel.type][rel.id].attributes || {};
  }
  return undefined;
};

export const parseMember = (resource, includedIndex) => {
  const attrs = resource?.attributes || {};
  const relationship = pick(attrs, ['relationship', 'derived-relationship', 'kind', 'relationship-kind']);

  return {
    id: resource.id,
    firstName: pick(attrs, ['first-name', 'firstName', 'name'], ''),
    lastName: pick(attrs, ['last-name', 'lastName'], ''),
    // "What will [baby] call them" — the recorded label, e.g. "Mommy", "Auntie Jo".
    label: pick(attrs, ['label', 'recorded-label', 'recordedLabel', 'nickname'], ''),
    relationship,
    side: pick(attrs, ['side']),
    blood: pick(attrs, ['blood', 'is-blood']),
    throughMember: pick(attrs, ['through-member', 'through-member-id', 'throughMember']),
    birthdate: pick(attrs, ['birthdate', 'birth-date', 'date-of-birth', 'dob']),
    position: pick(attrs, ['position', 'order'], 0),
    selfIdentified: pick(attrs, ['self-identified', 'selfIdentified'], false),
    pathKnown: pick(attrs, ['path-known', 'pathKnown'], true),
    invitePending: pick(attrs, ['invite-pending', 'invitePending', 'invited'], false),
    hasVideo: pick(attrs, ['has-video', 'hasVideo', 'video-uploaded'], false),
    'personal-name': pick(attrs, ['personal-name', 'personalName', 'label'], ''),
    'date-of-birth': pick(attrs, ['date-of-birth', 'dateOfBirth', 'dob'], ''),
    relationshipKind: relationship,
    // Underlying Member id (media is owned by the Member, not the family_member link).
    memberId:
      pick(attrs, ['member-id', 'member_id', 'memberId'])
      || resource?.relationships?.member?.data?.id
      || undefined,
    imageId:
      resource?.relationships?.image?.data?.id ||
      resource?.relationships?.avatar?.data?.id ||
      undefined,
    image: resolveImage(resource, includedIndex),
    video: resolveVideo(resource, includedIndex),
    raw: attrs,
  };
};

// Turn a raw SWR/axios response into grouped, position-sorted members.
export const parseFamilyTree = (axiosResponse) => {
  const body = axiosResponse?.data;
  if (!body || !body.data) return { groups: [], members: [], meta: {} };
  const includedIndex = buildIncludedIndex(body.included);
  const list = Array.isArray(body.data) ? body.data : [body.data];
  const members = body.included.map((res) => parseMember(res, includedIndex));
  const familyTreeRelatives = body.included.map(({ type,
    attributes
  }) => type === "family-tree-relatives" ?
      attributes
      : null).filter(Boolean);

  const byGroup = body.included.reduce((acc, member) => {
    const key = groupForRelationship(member?.attributes?.relationship);
    const memberAttr = parseMember(member, includedIndex)
    acc[key] = acc[key] || [];
    acc[key].push({ id: member.id, type: member.type, ...memberAttr });
    return acc;
  }, {});

  const groups = GROUPS.map((group) => ({
    ...group,
    members: (byGroup[group.key] || []).sort((a, b) => (a.position || 0) - (b.position || 0)),
  })).filter((group) => group.members.length > 0);

  return { groups, members, meta: body.meta || {}, familyTreeRelatives };
};

// Invite link from the talking_family_tree resource attributes. The API returns
// `invitation-url`; older/alternate keys kept as fallbacks.
export const pickInviteUrl = (attrs = {}) =>
  attrs['invitation-url'] || attrs['invite-url'] || attrs['share-url'] || '';


export const pickTftUrl = (attrs = {}) =>
  attrs['family-tree-url'] || '';

// Parse GET /talking_family_tree — the resource attributes plus the
// `talking-family-tree-topics` included in the response. Topics are grouped into
// sections by their category (preserving first-seen order) for the wizard's
// accordion. Attribute names are read defensively across likely JSON:API keys.
export const parseTalkingFamilyTree = (response) => {
  const body = response?.data;
  const attrs = body?.data?.attributes || {};
  const included = Array.isArray(body?.included) ? body.included : [];
  const topics = included
    .filter((r) => r && typeof r.type === 'string' && r.type.includes('topic'))
    .map((r) => {
      const a = r.attributes || {};
      return {
        id: r.id,
        label: pick(a, ['description', 'label', 'name', 'prompt', 'question', 'body', 'text'], ''),
        category: pick(a, ['category', 'section', 'group', 'heading', 'kind'], 'Video Message Ideas'),
        position: pick(a, ['position', 'order'], 0),
        selected: pick(a, ['selected', 'is-selected', 'isSelected'], false),
      };
    });

  // Group topics into sections, preserving the order categories first appear.
  const order = [];
  const byCategory = {};
  topics.forEach((t) => {
    if (!byCategory[t.category]) {
      byCategory[t.category] = [];
      order.push(t.category);
    }
    byCategory[t.category].push(t);
  });
  const sections = order.map((category) => ({
    key: category,
    title: category,
    ideas: byCategory[category]
      .sort((x, y) => (x.position || 0) - (y.position || 0))
      .map((t) => ({ key: t.id, label: t.label })),
  }));

  const selectedTopicIds = topics.filter((t) => t.selected).map((t) => t.id);
  return {
    attrs,
    inviteUrl: pickInviteUrl(attrs),
    invitationCopy: attrs['invitation-copy'] || '',
    topics,
    sections,
    selectedTopicIds,
  };
};

// Best display URL from an image/media attributes object.
export const imageUrlFromAttrs = (attrs) =>
  attrs?.tmpUrl
  || attrs?.['image-versions']?.[5]?.url
  || attrs?.['image-versions']?.[3]?.url
  || attrs?.['asset-url']
  || '';

// Crop/zoom for react-avatar-editor display, read from a media/attrs object (or the
// in-progress cropper value { zoom, 'crop-left', 'crop-top' }).
export const cropParamsFromAttrs = (attrs) => ({
  zoom: Number(attrs?.zoom) || 1,
  position:
    attrs && (attrs['left'] != null || attrs['top'] != null)
      ? { x: Number(attrs['left']) || 0, y: Number(attrs['top']) || 0 }
      : undefined,
});

// Display label for a member row: "First · What baby calls them" (falls back to relationship).
export const memberRowSubtitle = (member) =>
  member?.['personal-name'] || member.label || member.relationship || '';

export const memberFullName = (member) =>
  [member?.['firstName'], member?.['last-name']].filter(Boolean).join(' ').trim() || member?.['first-name'] || '';

const looksLikeFamilyMember = (r) =>
  r?.attributes
  && typeof r.type === 'string'
  && !r.type.includes('image')
  && !r.type.includes('video')
  && !r.type.includes('topic')
  && (
    'personal-name' in r.attributes
    || 'relationship' in r.attributes
    || 'relationship-kind' in r.attributes
    || 'label' in r.attributes
  );

const videoUrlFromAttrs = (attrs) => attrs?.['has-a-portrait-video'] ? attrs['hls-portrait'] :
  attrs?.['asset-url']
  || attrs?.['mp4']
  || '';

const videoPreviewUrlFromAttrs = (attrs) =>
  attrs?.['preview-url']
  || '';

// Parse GET /v3/public/talking_family_trees/:slug into a flat, ordered list of
// relatives for the QR / "click to watch" view.
export const parsePublicFamilyTree = (response) => {
  const body = response?.data;
  const included = Array.isArray(body?.included) ? body.included : [];
  const includedIndex = buildIncludedIndex(included);

  const members = included
    .filter(looksLikeFamilyMember)
    .map((r) => {
      const a = r.attributes || {};
      const videoAttrs = resolveVideo(r, includedIndex);

      return {
        id: r.id,
        // What the baby calls them (bold), e.g. "Mommy".
        personalName: pick(a, ['personal-name', 'personalName', 'label', 'name'], ''),
        // Relationship to the baby (coloured), e.g. "Mother".
        relationship: pick(a, ['relationship', 'relationship-kind', 'derived-relationship', 'kind'], ''),
        videoUrl: videoUrlFromAttrs(videoAttrs) || a['video-url'] || a['video_url'] || '',
        videoPreviewUrl: videoPreviewUrlFromAttrs(videoAttrs),
        image: resolveImage(r, includedIndex),
        position: pick(a, ['position', 'order'], 0),
      };
    })
    .sort((x, y) => (x.position || 0) - (y.position || 0));

  return { members, attrs: body?.data?.attributes || {}, meta: body?.meta || {} };
};

// Parse the public invite (recipient flow) from GET /v3/public/talking_family_trees/:slug
// → baby name, personal invite copy, and the selected "what to say" topic labels.
export const parsePublicInvite = (response) => {
  const parsed = parseTalkingFamilyTree(response);
  const attrs = parsed.attrs || {};
  const babyName = attrs['first-name']
    || 'the baby';
  const avatarRelationShip = response?.data?.data?.relationships?.avatar?.data
  const selected = parsed.topics.filter((t) => t.selected).map((t) => t.label);
  const topics = (selected.length ? selected : parsed.topics.map((t) => t.label)).filter(Boolean);
  return { babyName, invitationCopy: parsed.invitationCopy, topics, attrs, avatarRelationShip };
};
